// Help screen (UI-6): the player guide. Opened from the title and pause menus and
// by the "?" hint. Client-only (ENG-1).
//
// Controls mirror input.ts; commands come from the command registry. Everything
// else is written guidance for systems the game actually has — a player cannot
// discover hoe-gated tilling, farmland moisture, or the cotton→string chain by
// poking at blocks, and an unexplained drowning meter is just a death.
import { h, clear } from '../h';
import { commandHelpLines } from '../../../sim/game/commands/core';
import { SCR, TICK } from '../../../sim/config';
import { VERB_SIGNATURES, VERB_SLICES } from '../../../sim/script/api';
import { VERB_CAPABILITIES } from '../../../sim/script/capabilities';

/** [key, action] rows mirroring the bindings in input.ts / chat.ts / the HUD. */
const CONTROLS: ReadonlyArray<readonly [string, string]> = [
  ['W A S D', 'Move'],
  ['Space', 'Jump  ·  double-tap = fly (creative)  ·  fly up  ·  swim up'],
  ['Shift', 'Sneak / sprint  ·  fly down'],
  ['Mouse', 'Look around'],
  ['Left click', 'Break block (hold)  ·  attack mob'],
  ['Right click', 'Place block  ·  use / open workstation  ·  till soil (hoe)'],
  ['Middle click', 'Pick block'],
  ['1 – 9  /  Wheel', 'Select hotbar slot'],
  ['E', 'Inventory  ·  creative palette'],
  ['T', 'Chat'],
  ['/', 'Command (needs cheats — see Commands)'],
  ['M', 'Toggle minimap'],
  ['F3', 'Debug overlay'],
  ['Esc', 'Pause  ·  close any screen'],
];

/** The `code` row kind (ARCH §27.17). A guide row used to be a `[term, text]` pair
 *  and nothing else, which is fine for prose and useless for a scripting tutorial:
 *  code has to keep its indentation, its line breaks and its column alignment, so it
 *  renders as a `<pre>` rather than flowing text. The block carries its own monospace
 *  styling instead of a stylesheet rule so that a guide section is self-contained. */
interface CodeRow {
  /** Source text, rendered verbatim. Trailing whitespace is trimmed on render. */
  code: string;
  /** Optional one-line lead-in above the block. */
  caption?: string;
}

/** One row of a section: the existing `[term, text]` pair, or a code block. */
type Row = readonly [string, string] | CodeRow;

const isCode = (row: Row): row is CodeRow => 'code' in row;

/** A guide section: a heading and a list of paragraphs, [term, text] rows or code. */
interface Section {
  title: string;
  intro?: string;
  rows?: readonly Row[];
  notes?: readonly string[];
}

const GETTING_STARTED: Section[] = [
  {
    title: 'Your first few minutes',
    intro: 'You start with nothing. Everything below comes out of the world.',
    rows: [
      ['1. Punch a tree', 'Breaking logs by hand gives you oak logs. This also unlocks the planks recipe in your recipe book.'],
      ['2. Make planks', 'Open your inventory (E), hit Recipe Book, and click the planks recipe — it fills the 2×2 grid for you from what you are carrying.'],
      ['3. Crafting table', 'Four planks in the 2×2 grid. Place it and right-click for a 3×3 grid, which most recipes need.'],
      ['4. Tools', 'Planks → sticks, then sticks plus planks/cobblestone for a pickaxe. Stone needs a pickaxe to drop anything.'],
      ['5. Shelter', 'Hostile mobs spawn in the dark. Have walls and a torch before nightfall.'],
    ],
  },
  {
    title: 'The recipe book',
    intro: 'Both the inventory (2×2) and the crafting table (3×3) have one.',
    rows: [
      ['How recipes unlock', 'You learn a recipe by obtaining one of its ingredients — picking it up, or crafting it. Chop a log and you learn planks; craft planks and you learn the crafting table, sticks and more.'],
      ['Show: All / Craftable', 'Toggles between everything you know and only what you can make right now. Recipes you cannot afford are dimmed rather than hidden.'],
      ['Search', 'Filters by result name.'],
      ['Clicking a recipe', 'Fills the grid from your inventory. The inventory book only lists recipes that fit 2×2 — anything bigger needs the table.'],
    ],
  },
];

const SURVIVAL: Section[] = [
  {
    title: 'Staying alive',
    rows: [
      ['Hearts', 'Your health. It refills slowly while you are well fed.'],
      ['Drumsticks', 'Hunger. It drains as you sprint, jump and mine; eat before it empties or you will starve.'],
      ['Bubbles', 'Air. They appear only underwater and drain while your head is submerged. When they run out you start drowning — surface before then.'],
      ['Getting out of water', 'Swim up against the shoreline and jump: once your feet touch the bottom you get a normal jump, which clears a one-block bank.'],
      ['Falling', 'Long drops hurt. Water breaks a fall.'],
    ],
  },
  {
    title: 'Tools and blocks',
    rows: [
      ['Right tool, right block', 'Stone and ore need a pickaxe, dirt and sand a shovel, wood an axe. Mining with the wrong tool is slow and often drops nothing at all.'],
      ['Tiers', 'Wood → stone → iron → diamond → netherite. A tool that is too weak for a block will not yield it, however long you hold the button.'],
      ['Durability', 'Tools wear out. Hover an item to see its remaining durability, damage and enchantments.'],
    ],
  },
];

const FARMING: Section[] = [
  {
    title: 'Preparing land',
    rows: [
      ['You need a hoe', 'Right-click dirt or grass with any hoe to turn it into farmland. Bare hands will not do it. The hoe takes durability damage each time.'],
      ['Water matters', 'Farmland within 4 blocks of water stays hydrated and grows crops about twice as fast. Dry farmland still works, just slowly.'],
      ['Light matters', 'Crops need light level 9 or better — daylight, or a torch nearby.'],
      ['Do not trample', 'Jumping on farmland turns it back into dirt and destroys what was planted.'],
    ],
  },
  {
    title: 'Sowing and harvesting',
    rows: [
      ['Seeds', 'Break tall grass for wheat and cotton seeds. Carrots and potatoes plant themselves — the vegetable is the seed.'],
      ['Planting', 'Right-click farmland with seeds in hand.'],
      ['Growth', 'Crops advance through 8 stages (beetroot 4). Harvest only when fully grown, or you get your seed back and nothing else.'],
      ['Bone meal', 'Skips several growth stages instantly.'],
    ],
  },
  {
    title: 'Hydroponics',
    intro: 'A late-game upgrade over open soil, once you have iron.',
    rows: [
      ['What it is', 'A hydroponic tray: glass, iron ingots and a water bucket. It holds its own nutrient water.'],
      ['Why bother', 'It is always fully hydrated, so crops grow at the fast rate with no irrigation at all — no canals dug through the field, no water hauled anywhere.'],
      ['Better yield', 'A mature crop grown on a tray drops noticeably more produce than the same crop on farmland.'],
      ['Use it where water will not go', 'Underground farms, towers, the nether — anywhere a water source is awkward.'],
    ],
  },
  {
    title: 'Cotton, string and fishing',
    intro: 'String gates a lot of the early game, and cotton is how you farm it.',
    rows: [
      ['Cotton', 'Seeds come from tall grass. Plant on farmland or a tray like any other crop; harvest for cotton plus more seeds.'],
      ['String', 'Two cotton makes string in the 2×2 grid — no crafting table, no spiders.'],
      ['What string unlocks', 'Fishing rod, bow, lead, scaffolding and wool.'],
      ['Fishing', 'Right-click water with a rod to cast, then right-click again once you get a bite. Catches include cod, salmon, tropical fish and pufferfish — plus the occasional piece of junk or an enchanted book.'],
    ],
  },
];

const MULTIPLAYER: Section[] = [
  {
    title: 'Land claims',
    intro: 'On a server, claims give you exclusive build rights over a patch of ground.',
    rows: [
      ['Claim blocks', 'You start with enough for roughly a 12×12 plot and earn more the longer you play, up to a cap.'],
      ['/claim [radius]', 'Claims a square around you — radius 8 by default, so 17×17. It fails if it overlaps someone else or exceeds what you have earned.'],
      ['/claims', 'Tells you who owns the ground you are standing on and how many claim blocks you have left.'],
      ['/trust <player>', 'Lets a friend build inside your claim. /untrust takes it back.'],
      ['/unclaim', 'Releases the claim you are standing in and refunds its blocks.'],
      ['What is protected', 'A claim covers the whole column, bedrock to sky — nobody can tunnel in from below. Outside a claim, land is open to everyone.'],
    ],
  },
];

// ── Scripting (ARCH §27.17, SCR-13) ──────────────────────────────────────────
// The in-game tutorial: eleven parts, in §27.17's order, written for a cadet who
// has never programmed. It is a section of the existing guide rather than a new
// screen or a hosted site, so it is one Esc away from the thing being explained.
//
// The worked examples are READ FROM `data/collections/examples/`, never retyped:
// `skittish.tfs` and `patrol.tfs` are binding copy (PLAN §5.1) and the test suite
// asserts them character for character against the plan, so a pasted second copy
// here would be free to drift out of the assertion's sight.

/** The five §27.17 examples, raw. Bundled by Vite; the same files the unit test
 *  compiles and the editor's "Load example" picker loads. */
const EXAMPLE_SOURCES = import.meta.glob('/data/collections/examples/*.tfs', {
  eager: true, query: '?raw', import: 'default',
}) as Record<string, string>;

const example = (name: string): string => EXAMPLE_SOURCES[`/data/collections/examples/${name}.tfs`] ?? '';

/** How often a mob's `on tick` runs (§27.20: 20 TPS ÷ the stagger divisor). */
const MOB_HZ = TICK.TPS / SCR.MOB.TICK_DIVISOR;

/** Signature + one line of plain English per verb, for part 11. The TABLE itself is
 *  generated from `VERB_SIGNATURES`/`VERB_SLICES`/`VERB_CAPABILITIES` — the real
 *  sandbox — so a verb added to the language cannot quietly go undocumented; this map
 *  only supplies the prose, and a verb missing from it still lists. */
const VERB_HELP: Readonly<Record<string, readonly [string, string]>> = {
  getblock: ['getblock(x, y, z)', 'The name of the block in that cell.'],
  setblock: ['setblock(x, y, z, name)', 'Write one cell.'],
  fill: ['fill(x1, y1, z1, x2, y2, z2, name)', 'Write a box of cells in one go.'],
  summon: ['summon(name, x, y, z)', 'Spawn an entity.'],
  give: ['give(item, count)', 'Hand the nearby player an item.'],
  say: ['say(text)', 'One line of chat, rate-limited.'],
  detect: ['detect(x, y, z, name)', 'Is that cell that block? true or false.'],
  nearestEntity: ['nearestEntity(x, y, z)', 'The closest entity\'s position, or the point you asked about when there is none.'],
  nearestPlayer: ['nearestPlayer()', 'The closest player\'s position, measured from here — you cannot probe at arm\'s length.'],
  playerWithin: ['playerWithin(radius)', 'Is a player this close?'],
  entityWithin: ['entityWithin(radius, type)', 'Is an entity of that type this close?'],
  'aircraft.setControl': ['aircraft.setControl(pitch, roll, throttle)', 'Fly the aircraft this block is wired to.'],
  'self.moveTo': ['self.moveTo(point)', 'Steer toward a point, at this mob\'s own speed.'],
  'self.moveAway': ['self.moveAway(point)', 'Steer away from a point.'],
  'self.face': ['self.face(yaw)', 'Turn to a compass direction.'],
  'self.faceAt': ['self.faceAt(point)', 'Turn to look at a point.'],
  'self.jump': ['self.jump()', 'Ask for a jump this dispatch.'],
  'self.setTarget': ['self.setTarget(id)', 'Fight that entity. The id comes from an event, never from thin air.'],
  'self.clearTarget': ['self.clearTarget()', 'Stop fighting.'],
  'self.attack': ['self.attack()', 'Strike the current target — the host decides reach and cooldown, so you may ask every dispatch.'],
  'self.hurt': ['self.hurt(amount)', 'Damage this mob.'],
  'self.heal': ['self.heal(amount)', 'Heal this mob, up to its own maximum.'],
  'self.despawn': ['self.despawn()', 'Remove this mob. Not a death: it drops nothing.'],
  'mem.get': ['mem.get(key, fallback)', 'Read one memory key. The fallback is required.'],
  'mem.set': ['mem.set(key, value)', 'Write a number, a string or true/false.'],
  handled: ['handled()', 'Skip the built-in behaviour for this dispatch only.'],
  list: ['list(a, b, …)', 'Build a list.'],
  at: ['at(list, i)', 'The i-th value, counting from 0.'],
  len: ['len(list)', 'How many values.'],
  push: ['push(list, value)', 'A NEW list with the value on the end — lists never change in place.'],
  random: ['random(lo, hi)', 'A seeded number in that range.'],
  chance: ['chance(p)', 'true with probability p.'],
  vec3: ['vec3(x, y, z)', 'A point. Read it back with .x, .y and .z.'],
  distance: ['distance(a, b)', 'How far apart two points are.'],
  abs: ['abs(n)', 'Drop the minus sign.'],
  floor: ['floor(n)', 'Round down.'],
  ceil: ['ceil(n)', 'Round up.'],
  round: ['round(n)', 'Round to the nearest whole number.'],
  sqrt: ['sqrt(n)', 'Square root.'],
  sign: ['sign(n)', '-1, 0 or 1.'],
  min: ['min(a, b)', 'The smaller of two numbers.'],
  max: ['max(a, b)', 'The larger of two numbers.'],
  clamp: ['clamp(n, lo, hi)', 'Keep a number inside a range.'],
};

const SLICE_TEXT: Readonly<Record<string, string>> = {
  both: 'blocks + mobs',
  block: 'blocks only',
  mob: 'mobs only',
};

/** Part 11's table, generated from the verb table that IS the sandbox (§27.5). */
function verbReferenceRows(): Row[] {
  return Object.keys(VERB_SIGNATURES).map((verb): Row => {
    const [sig, blurb] = VERB_HELP[verb] ?? [verb, ''];
    const slice = SLICE_TEXT[VERB_SLICES[verb] ?? 'both'] ?? 'blocks + mobs';
    const cap = VERB_CAPABILITIES[verb];
    const tail = `${slice} · ${cap ? `needs ${cap}` : 'no capability'}`;
    return [sig, blurb ? `${blurb}  (${tail})` : `(${tail})`] as const;
  });
}

const SCRIPTING: Section[] = [
  {
    title: '1. What a script is, and where it lives',
    intro: 'A script is a short list of instructions the game runs for you, on a schedule you never have to set. It is content — like a texture, a recipe or a mob\'s health — and not a mod: nothing here is installed, compiled outside the game, or able to reach your computer.',
    rows: [
      ['Three homes, one language', 'A script block you place in the world; a mob TYPE, so every sheep runs it; or ONE entity, so only that sheep does. The language is identical in all three — only the list of things it may do changes (part 11).'],
      ['A block script', 'Right-click a script block to open its editor. It wakes when redstone power changes next to it.'],
      ['A mob script', 'Content editor, Mobs, then the Script tab. Every mob of that type runs it.'],
      ['Just this one animal', '"Attach to this entity" overrides the type for one creature, and travels with it in the save.'],
      { caption: 'The smallest script that does anything at all:', code: 'on redstone {\n  say("Someone pulled the lever.")\n}' },
      ['Where the examples are', 'The editor\'s "Load example" picker ships five: skittish, patrol, guard, greeter and door_opener. They are an OPT-IN collection, so a world you just created contains no scripted mobs at all — turning them on is a choice you make, not something the game did while you were not looking.'],
      ['Why you cannot break anything', 'There is no loop that runs forever, no clock, no network and no file. A script may only name the verbs in part 11, and the parser refuses every other name — which is why "fetch", "window" and "import" are not dangerous here: they are simply not words.'],
    ],
  },
  {
    title: '2. Values and variables',
    intro: 'Five kinds of value, one way to name them, and one surprise (%).',
    rows: [
      ['number', '12, -3, 0.5. The usual + - * /, and % for the remainder: 7 % 3 is 1. That remainder is the trick behind a route that wraps back to its start.'],
      ['string', 'Text inside quotes: "hello".'],
      ['bool', 'true or false — what a question like playerWithin(6) answers with.'],
      ['vec3', 'A point in the world: vec3(10, 64, 10). Read the parts back with .x, .y and .z.'],
      ['list', `Several values in order: list("a", "b"). at(l, 0) is the first, len(l) counts them, push(l, v) hands back a NEW list — a list never changes in place. Up to ${SCR.LIST_MAX} values.`],
      ['let names a value', 'let i = 0. A name you never declared is a compile error rather than a silent mistake, and that is deliberate: a typo should be caught at save time, not three days later.'],
      ['// is a comment', 'Everything after // on a line is for you, not for the game.'],
      { caption: 'Values, a name, the remainder, and the else-if ladder:', code: 'let names = list("Ash", "Bex", "Cyn")\nlet i     = 7 % len(names)   // 7 % 3 = 1\n\nif i == 0 {\n  say("first")\n} else if i < len(names) {\n  say(at(names, i))          // "Bex"\n} else {\n  say("off the end")\n}' },
    ],
  },
  {
    title: '3. Events and fall-through',
    intro: 'A script does not run continuously. It is DISPATCHED: woken for one event, run once from top to bottom, and put away again.',
    rows: [
      ['on <event> { … }', 'A handler. Its body runs only when that event is the one being dispatched.'],
      ['The thing authors get wrong', 'Every statement OUTSIDE a handler runs on EVERY dispatch, in order, BEFORE any handler runs. Top-level code is not startup code. A let at the top of the file is re-evaluated from scratch on every single dispatch, whatever the event was, and it is forgotten again the moment the dispatch ends. If you want something remembered from one dispatch to the next, that is memory (part 5) — never a top-level let.'],
      { caption: 'Read this in dispatch order: the two top-level lines, then one handler.', code: '// Runs on EVERY dispatch — tick, hurt, interact, all of them.\nlet route = list(vec3(10, 64, 10), vec3(20, 64, 10))\nlet home  = at(route, 0)\n\non tick {\n  // Only now, and only when the event was a tick.\n  self.moveTo(home)\n}\n\non hurt {\n  // `route` and `home` were rebuilt before we got here, too.\n  self.moveAway(home)\n}' },
      ['Fall-through', 'A mob script runs BEFORE the animal\'s built-in behaviour — and then the built-in behaviour runs as well. Your script ADDS to the animal; it does not replace it. Grazing, breeding, panicking when hurt and pathfinding all keep working, and everything your script does not mention still happens.'],
      ['handled()', 'Call it to skip the built-in behaviour for THIS dispatch only. It is never sticky: on the next dispatch the archetype is back unless you call it again. That is why a six-line script can make a sheep skittish without anyone having to re-implement being a sheep.'],
      { caption: 'skittish.tfs — six lines of behaviour, and everything it does not say still works.', code: example('skittish') },
      ['The events a mob gets', `tick (${MOB_HZ} times a second), hurt, target, death and interact.`],
      ['The events a block gets', 'redstone today. placed, broken, neighbor and random_tick arrive with the block lifecycle bus (part 8).'],
    ],
  },
  {
    title: '4. Making a mob move',
    intro: 'A script never writes a position. It states an intention, and the mob\'s own movement code decides whether that intention can be honoured.',
    rows: [
      ['self.moveTo(point)', 'Steer toward a point — at this mob\'s own speed, around walls, and not off a cliff.'],
      ['self.moveAway(point)', 'The same wish, mirrored: run from there.'],
      ['self.face(yaw) · self.faceAt(point)', 'Turn on the spot, without going anywhere.'],
      ['self.jump()', 'Ask for a jump on this dispatch.'],
      ['self.pos · self.x .y .z', 'Where the mob is right now. A mob\'s position has decimals in it; a block coordinate does not, so it rounds down when a verb needs a cell.'],
      { caption: 'Walk to the water, and when you get there, look at it.', code: 'on tick {\n  let water = vec3(64, 62, 30)\n  if distance(self.pos, water) > 3 {\n    self.moveTo(water)\n  } else {\n    self.faceAt(water)\n    self.jump()\n  }\n  handled()\n}' },
      ['Why it is a wish and not a teleport', 'There is no verb that sets a position, on purpose. If there were, a mob could be walked through a wall or into the ground — and two machines replaying the same world would disagree about where it ended up, which breaks saves, servers and replays all at once.'],
    ],
  },
  {
    title: '5. Memory and state machines',
    intro: 'Memory is the mob\'s own notebook: a small bag of named values that survives the dispatch, the save file, and an edit to the script.',
    rows: [
      ['mem.get(key, fallback)', 'Read one key. The fallback is required, so the very first read is never a hole in the middle of a sum.'],
      ['mem.set(key, value)', 'Write a number, a string, or true/false.'],
      ['How small is small', `${SCR.MEM.KEYS} keys per animal, and a string of at most ${SCR.MEM.STR_MAX} characters. It saves with the animal and is never sent over the network — it is the mob's private notebook, not shared state.`],
      ['A state machine, demystified', 'Read a key, decide what to do, write the key back. That is the whole idea: the key IS the state, and the decision is how it moves. patrol.tfs below keeps one number — which waypoint it is heading for — and % walks it round the list forever.'],
      { caption: 'patrol.tfs — a route in a list, the position in memory.', code: example('patrol') },
      ['Memory outlives the script', 'Edit a script and every live mob of that type picks up the new one immediately, keeping its memory. Keys the new script never reads are simply dead weight until the animal despawns.'],
    ],
  },
  {
    title: '6. Randomness',
    intro: 'There is random, there is chance, and there is deliberately no Math.random.',
    rows: [
      ['random(lo, hi)', 'A number somewhere in that range.'],
      ['chance(p)', 'true with probability p — chance(0.25) is a one-in-four.'],
      ['Why Math.random does not exist here', 'The world has to be reproducible: the same world ticked twice must come out identical, a server and your own machine must agree, and a recorded replay must replay. An ordinary random number generator breaks all three. Every draw here comes from a stream seeded by the world seed, this entity and this tick — unpredictable to you, identical on every machine, and the same on the second run.'],
      { caption: 'A cow that occasionally says something, and occasionally wanders.', code: 'on tick {\n  if chance(0.02) {\n    say("moo")\n  }\n  if chance(0.1) {\n    self.moveTo(vec3(random(-64, 64), self.y, random(-64, 64)))\n    handled()\n  }\n}' },
      ['A dice roll you cannot re-roll', 'The stream is re-derived per dispatch, so you cannot call chance() twenty times hunting for the answer you wanted — that is a feature, not a limit.'],
    ],
  },
  {
    title: '7. Talking to other entities',
    intro: 'One wolf notices something and the whole pack hears about it. That is a signal — and it is also the fastest way to bring a server to its knees, so it comes with limits.',
    rows: [
      ['Not in this build yet', 'emit and on signal arrive with the signal work, together with the example that uses them (pack.tfs). The shape below is what they will look like, and the limits are why they look like that.'],
      ['emit(name, payload)', 'Shout one named signal, carrying one single value, to every entity inside the signal radius. One value, not a list: a pack that passes lists around is a pack that copies them, several hundred times a tick.'],
      ['on signal', 'The other half. event.name is what was shouted; event.payload is what came with it.'],
      { caption: 'pack.tfs — a wolf spots a target; the pack hears about it.', code: 'on target {\n  emit("hunt", event.source)\n}\n\non signal {\n  if event.name == "hunt" {\n    self.setTarget(event.payload)\n  }\n}' },
      ['Why a cascade gets clipped', 'A shouts, B hears and shouts, C hears and shouts. Three limits stop the pack ringing like a bell instead of converging on the target: a maximum depth of hops, a cap on how many dispatches one tick may cause, and a same-tick dedupe — no entity hears the same signal name twice in one tick, however many neighbours shouted it.'],
      ['Clipped is counted, not hidden', 'Whatever the limits drop is counted and shown in the editor\'s diagnostics, so "my pack only half works" is a number you can read rather than a mystery.'],
      ['Until then, there is say()', `The blunt version: everyone in earshot reads it. Rate-limited to one line per mob per ${SCR.SAY.ENTITY_TICKS / TICK.TPS} second and ${SCR.SAY.WORLD_PER_SEC} a second across the whole world — over-limit lines are dropped, never an error.`],
    ],
  },
  {
    title: '8. Blocks that react',
    intro: 'The other host. A block script has no self to steer and no memory bag — a block does not walk, and what a block remembers is already saved as its block-entity data — but it reads and writes the world far more freely than a mob does.',
    rows: [
      ['on redstone', 'Fires when the redstone power beside the block changes. event.power is the new level; self.x, self.y and self.z are the block\'s own cell.'],
      ['setblock and fill', `A block script may fill up to ${SCR.FILL_MAX} cells at once. A mob may not fill at all, and may setblock at most ${SCR.MOB.SETBLOCK_MAX} cells per dispatch — one enderman-ish mob rearranging a few blocks is a feature; seventy of them filling 4096 cells each is world deletion.`],
      { caption: 'door_opener.tfs — the block half of the language, end to end.', code: example('door_opener') },
      ['Arriving with the lifecycle bus', 'on placed, on broken, on neighbor and on random_tick: a block that reacts to being built, to its neighbours changing, or that changes on its own over time (that last one is growth.tfs).'],
      ['Nothing fires in the middle of an edit', 'Lifecycle events are queued and drained on the next tick, never inside the edit that caused them. It is the one rule that keeps a script-driven contraption behaving exactly like a redstone one.'],
    ],
  },
  {
    title: '9. Capabilities and trust',
    intro: 'A script declares what it is allowed to reach, in a block at the very top. The game then checks the declaration against the code — it does not take your word for it.',
    rows: [
      ['capabilities { … }', 'A list of names, before any handler: world.read, world.write, entity.spawn, player.give, chat, aircraft, self, signal.'],
      ['Checked, not documented', 'Declare world.read and then call setblock, and the script does not compile — the error names the verb AND the capability it needs. Hiding the call inside a fn does not help: the check follows the calls.'],
      ['What a server owner sees', 'The install prompt for an imported collection shows the COMPUTED set — what the code actually reaches — not what its author typed. A script cannot be made to look tamer than it is.'],
      { caption: 'Declared above, used below. The commented-out line would not compile.', code: 'capabilities { world.read, self }\n\non tick {\n  if playerWithin(4) {\n    self.moveAway(nearestPlayer())        // needs `self`, declared\n    // setblock(self.x, self.y, self.z, "stone")\n    //   ^ refused: that verb needs world.write\n  }\n}' },
      ['Leaving the block out', 'A script with no capabilities block declares exactly what it uses. That is not a loophole — it is how every script written before the block existed keeps working unchanged.'],
    ],
  },
  {
    title: '10. Budgets, errors and quarantine',
    intro: 'Every dispatch gets a fixed allowance of work. Reading the op read-out is how you find out about a problem before your mob goes quiet.',
    rows: [
      ['The op budget', `A mob may spend ${SCR.MOB.OP_BUDGET} ops per dispatch; a script block, ${SCR.OP_BUDGET}. An "op" is roughly one statement or one piece of arithmetic. The editor shows what your script costs as you type.`],
      ['Refused at save, not at midnight', 'Before it saves, the editor works out the WORST case your script could cost. If that exceeds the budget, the save is refused then and there, naming the number and the line that crossed it. Because there is no "while" and no recursion, that worst case is always a finite number — which is what makes the refusal honest rather than a guess.'],
      ['Cadence', `A mob's on tick runs ${MOB_HZ} times a second, and animals are staggered so they do not all run on the same tick. Asking for the full ${TICK.TPS} Hz quarters the budget, so the cost per second is unchanged.`],
      ['When a dispatch fails', 'It aborts THAT dispatch and nothing else — never the tick, never the world, never another mob. The last errors are kept with their line and tick in the diagnostics panel.'],
      ['Quarantine', `${SCR.MOB.QUARANTINE_K} aborts in a row and the TYPE is quarantined: every mob of it falls back to its built-in behaviour until you edit the script or reload the world. Your sheep go back to being sheep instead of standing still looking broken.`],
      ['A broken edit does not brick anything', 'Save a script that does not compile and the last good program keeps running on every live mob, with the error shown to you. Nothing in the world goes quiet because you were mid-sentence.'],
    ],
  },
  {
    title: '11. Verb reference',
    intro: 'Every name a script may call. Anything else is a compile error — and that list, not a blocklist, IS the sandbox: there is no fetch, no window, no import, no timer and no file, because they are not words here. Alongside the verbs there are two read-only namespaces: self (.x .y .z .pos .powered) and event (.name .power).',
    rows: verbReferenceRows(),
  },
];

const TABS: ReadonlyArray<readonly [string, Section[]]> = [
  ['Getting Started', GETTING_STARTED],
  ['Survival', SURVIVAL],
  ['Farming', FARMING],
  ['Multiplayer', MULTIPLAYER],
  ['Scripting', SCRIPTING],
];

/** A `code` row: a `<pre>` (monospace by default, whatever the surrounding font is)
 *  with the panel's own dark-card look applied inline — `help.ts` grows the row kind
 *  without the stylesheet having to grow with it. */
function renderCode(row: CodeRow): HTMLElement {
  const block = h('pre', {
    class: 'bb-guide-code',
    'data-testid': 'guide-code',
    style: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '12px',
      lineHeight: '1.5',
      color: '#cfe3b4',
      background: 'rgba(0, 0, 0, 0.32)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '4px',
      padding: '8px 10px',
      margin: '6px 0',
      overflowX: 'auto',
      whiteSpace: 'pre',
      tabSize: '2',
    },
  }, row.code.replace(/\s+$/, ''));
  if (!row.caption) return block;
  return h('div', { class: 'bb-guide-codewrap' },
    h('div', { class: 'bb-guide-intro' }, row.caption), block);
}

function renderSections(sections: Section[]): HTMLElement {
  const wrap = h('div', { class: 'bb-guide' });
  for (const s of sections) {
    wrap.append(h('div', { class: 'bb-guide-h' }, s.title));
    if (s.intro) wrap.append(h('div', { class: 'bb-guide-intro' }, s.intro));
    for (const row of s.rows ?? []) {
      if (isCode(row)) { wrap.append(renderCode(row)); continue; }
      const [term, text] = row;
      wrap.append(h('div', { class: 'bb-guide-row' },
        h('div', { class: 'bb-guide-term' }, term),
        h('div', { class: 'bb-guide-text' }, text)));
    }
    for (const n of s.notes ?? []) wrap.append(h('div', { class: 'bb-muted' }, n));
  }
  return wrap;
}

function renderControls(): HTMLElement {
  return h('div', { class: 'bb-help' },
    ...CONTROLS.map(([k, v]) =>
      h('div', { class: 'bb-help-row' }, h('kbd', { class: 'bb-kbd' }, k), h('span', null, v))));
}

function renderCommands(): HTMLElement {
  return h('div', { class: 'bb-help bb-help-cmds' },
    ...commandHelpLines().map((line) => h('div', { class: 'bb-help-cmd' }, line)),
    h('div', { class: 'bb-muted' }, 'Commands need cheats. Turn them on from the pause menu (Cheats: On), or enable "Allow Cheats" when creating a world.'));
}

export function showHelp(onClose: () => void): HTMLElement {
  // Opaque (not `bb-pause`) so it fully covers whatever is behind — the title
  // menu or the paused game — rather than letting it bleed through.
  const root = h('div', { class: 'bb-screen' });
  document.body.append(root);
  const close = (): void => { document.removeEventListener('keydown', onKey, true); root.remove(); onClose(); };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);

  const body = h('div', { class: 'bb-help-body' });
  const tabsRow = h('div', { class: 'bb-creative-tabs' });

  const show = (render: () => HTMLElement, btn: HTMLElement): void => {
    clear(body);
    body.append(render());
    for (const el of tabsRow.children) el.classList.toggle('bb-tab-active', el === btn);
    body.scrollTop = 0;
  };

  const addTab = (label: string, render: () => HTMLElement): HTMLElement => {
    const btn = h('button', { class: 'bb-tab', 'data-help-tab': label }, label);
    btn.onclick = () => show(render, btn);
    tabsRow.append(btn);
    return btn;
  };

  const first = addTab(TABS[0]![0], () => renderSections(TABS[0]![1]));
  for (const [label, sections] of TABS.slice(1)) addTab(label, () => renderSections(sections));
  addTab('Controls', renderControls);
  addTab('Commands', renderCommands);

  root.append(
    h('div', { class: 'bb-title' }, 'How to Play'),
    h('div', { class: 'bb-panel bb-help-panel' },
      tabsRow,
      body,
      h('button', { class: 'bb-btn bb-primary', onclick: close }, 'Back'),
    ),
  );
  show(() => renderSections(TABS[0]![1]), first);
  return root;
}
